import { useEffect, useState } from 'react';

/**
 * What the backend says an order costs (chef-api `POST client/order-quote`, rules in orderPricing.js).
 * The checkout shows these numbers and does no price math of its own, so it can never disagree with what
 * placing the order charges.
 */
export interface OrderQuote {
    items: { productId: number, count: number, price: number, lineTotal: number, deliveryDay: number | null }[]
    deliveries: { deliveryDay: number | null, date: string, itemsTotal: number, cost: number }[]
    undatedItemsTotal: number
    itemsSubtotal: number
    deliveryTotal: number
    express: {
        available: boolean
        chosen: boolean
        zoneName: string | null
        fee: number | null
        free: boolean
        freeMinAmount: number
    }
    pickup: boolean
    total: number
}

const QUOTE_DELAY_MS = 120;

/**
 * Quote for an order-new style payload (null while there is nothing to price). Requests are debounced,
 * a late answer to an outdated payload is dropped, and the last good quote stays on screen while a new one loads.
 */
export function useOrderQuote(payload: any | null): { quote: OrderQuote | null, failed: boolean } {
    const [state, setState] = useState<{ quote: OrderQuote | null, failed: boolean }>({ quote: null, failed: false });
    const key = payload ? JSON.stringify(payload) : '';

    useEffect(() => {
        if (!key) {
            setState({ quote: null, failed: false });
            return;
        }
        let current = true;
        const timer = setTimeout(async () => {
            const token = localStorage.getItem('clientToken');
            if (!token || token === 'undefined') {
                return;
            }
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}client/order-quote`, {
                    method: 'POST',
                    headers: { Accept: 'application/json', Authorization: token, 'Content-Type': 'application/json' },
                    body: key,
                });
                if (!current) {
                    return;
                }
                if (!res.ok) {
                    setState({ quote: null, failed: true });
                    return;
                }
                setState({ quote: await res.json(), failed: false });
            } catch (err) {
                console.error(err);
                if (current) {
                    setState({ quote: null, failed: true });
                }
            }
        }, QUOTE_DELAY_MS);

        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [key]);

    return state;
}
